import { useEffect } from "react";

interface SeoProps {
  title: string;
  description: string;
  /** Path-only, e.g. "/features". Origin is taken from window.location. */
  canonicalPath?: string;
  /** Optional JSON-LD structured data (FAQPage, Product, etc.) */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const upsertMeta = (selector: string, attrs: Record<string, string>) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
};

const upsertCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SCRIPT_ID = "page-jsonld";

/**
 * Lightweight per-page SEO. Updates <title>, meta description, OG/Twitter,
 * canonical URL, and (optionally) injects JSON-LD structured data.
 *
 * Avoids react-helmet to keep bundle small and predictable.
 */
export function Seo({ title, description, canonicalPath, jsonLd }: SeoProps) {
  useEffect(() => {
    document.title = title;

    upsertMeta("meta[name='description']", { name: "description", content: description });
    upsertMeta("meta[property='og:title']", { property: "og:title", content: title });
    upsertMeta("meta[property='og:description']", { property: "og:description", content: description });
    upsertMeta("meta[name='twitter:title']", { name: "twitter:title", content: title });
    upsertMeta("meta[name='twitter:description']", { name: "twitter:description", content: description });

    if (typeof window !== "undefined") {
      const path = canonicalPath ?? window.location.pathname;
      upsertCanonical(`${window.location.origin}${path}`);
      upsertMeta("meta[property='og:url']", { property: "og:url", content: `${window.location.origin}${path}` });
    }

    // JSON-LD
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();
    if (jsonLd) {
      const tag = document.createElement("script");
      tag.id = SCRIPT_ID;
      tag.type = "application/ld+json";
      tag.text = JSON.stringify(jsonLd);
      document.head.appendChild(tag);
    }

    return () => {
      const stale = document.getElementById(SCRIPT_ID);
      if (stale) stale.remove();
    };
  }, [title, description, canonicalPath, JSON.stringify(jsonLd)]);

  return null;
}

export default Seo;
